import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { updateMe } from '../lib/api';
import { toast } from '../hooks/use-toast';

const ProfileDialog = ({ open, onOpenChange }) => {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [cp, setCp] = useState({ cur: '', next: '', conf: '' });
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (open && user) {
      setForm({
        display_name: user.display_name || user.name || '',
        default_owner_email: user.default_owner_email || user.email || '',
      });
    }
  }, [open, user]);

  if (!user) return null;

  const handleSave = async () => {
    try {
      setSaving(true);
      const updated = await updateMe(form);
      setUser(updated);
      toast({ title: 'Profile saved' });
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Failed', description: err.message });
    } finally {
      setSaving(false);
    }
  };

  const submitPw = async () => {
    if (cp.next !== cp.conf) {
      toast({ title: 'Passwords do not match', variant: 'destructive' });
      return;
    }
    try {
      setPwSaving(true);
      const { changePassword } = await import('../lib/api');
      await changePassword(cp.cur, cp.next);
      toast({ title: 'Password changed' });
      setCp({ cur: '', next: '', conf: '' });
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Could not change password', variant: 'destructive' });
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>User Profile</DialogTitle>
          <DialogDescription>
            Configure how you appear in Genba Pulse.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            {user.picture ? (
              
              <img
                src={user.picture}
                alt="Profile picture"
                className="w-14 h-14 rounded-full border-2 border-white shadow-sm"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center text-white text-xl font-semibold">
                {(user.name || user.email || '?')[0].toUpperCase()}
              </div>
            )}
            <div>
              <div className="font-semibold text-slate-900">{user.name}</div>
              <div className="text-sm text-slate-500">{user.email}</div>
            </div>
          </div>

          <div>
            <Label>Display name</Label>
            <Input
              value={form.display_name || ''}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              className="mt-1.5"
              placeholder="How your name appears in boards"
            />
          </div>

          <div>
            <Label>Default owner email</Label>
            <Input
              type="email"
              value={form.default_owner_email || ''}
              onChange={(e) => setForm({ ...form, default_owner_email: e.target.value })}
              className="mt-1.5"
              placeholder="Used when assigning tasks"
            />
            <p className="text-xs text-slate-500 mt-1">
              This email is pre-filled when creating boards & assigning tasks.
            </p>
          </div>

          <div className="border-t border-slate-200 pt-4">
            <div className="font-medium text-slate-900 mb-2">Change password</div>
            <div className="space-y-2">
              <Input
                type="password"
                value={cp.cur}
                onChange={(e) => setCp({ ...cp, cur: e.target.value })}
                placeholder="Current password"
                data-testid="profile-current-password"
              />
              <Input
                type="password"
                value={cp.next}
                onChange={(e) => setCp({ ...cp, next: e.target.value })}
                placeholder="New password"
                data-testid="profile-new-password"
              />
              <Input
                type="password"
                value={cp.conf}
                onChange={(e) => setCp({ ...cp, conf: e.target.value })}
                placeholder="Confirm new password"
                data-testid="profile-confirm-password"
              />
              <Button
                variant="outline"
                onClick={submitPw}
                disabled={pwSaving || !cp.cur || !cp.next}
                className="w-full"
                data-testid="profile-change-password-btn"
              >
                {pwSaving ? 'Updating…' : 'Update password'}
              </Button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0090D8] hover:bg-[#0079B5] text-white"
          >
            {saving ? 'Saving...' : 'Save profile'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileDialog;
